import React from 'react';
import { useNavigate } from 'react-router-dom';

const UpgradeModal = ({
  isOpen,
  onClose,
  trigger = 'general', // 'usage_limit', 'ocr_feature', 'health_tracker', 'points_exhausted', 'general'
  currentUsage = 0,
  monthlyLimit = 3,
  remainingPoints = null,
  requiredPoints = null,
  monthlyPoints = null
}) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  // トリガー別のメッセージとプラン提案を設定
  const getModalContent = () => {
    switch (trigger) {
      case 'usage_limit':
        return {
          title: '🚀 月間利用制限に達しました',
          message: `今月の相談回数が上限の${monthlyLimit}回に達しています。続けて相談するにはプランをアップグレードしてください。`,
          urgency: 'high',
          recommendedPlan: 'premium'
        };
      case 'points_exhausted':
        return {
          title: 'もっとつむぎとお話したいよね',
          message: remainingPoints !== null && monthlyPoints
            ? `今月のポイントが尽きちゃったみたい（${remainingPoints} / ${monthlyPoints}pt）。プランを変更すると、来月を待たずに続きを話せるよ。`
            : '今月のポイントが尽きちゃったみたい。プランを変更すると、来月を待たずに続きを話せるよ。',
          urgency: 'high',
          recommendedPlan: 'premium'
        };
      case 'ocr_feature':
        return {
          title: '🩸 血液検査OCR機能',
          message: '血液検査画像の自動解析機能はプレミアムプラン限定です。より詳細で専門的な栄養分析を体験してください。',
          urgency: 'medium',
          recommendedPlan: 'premium'
        };
      case 'health_tracker':
        return {
          title: '📊 体調スコア記録機能',
          message: '体調スコア記録・AI分析機能はプレミアムプラン限定です。日々の改善傾向を可視化して効果を実感してください。',
          urgency: 'medium',
          recommendedPlan: 'premium'
        };
      default:
        return {
          title: '✨ より充実した機能をお試しください',
          message: 'アップグレードすることで、より多くの相談回数と高度な機能をご利用いただけます。',
          urgency: 'low',
          recommendedPlan: 'premium'
        };
    }
  };
  
  const { title, message, urgency, recommendedPlan } = getModalContent();
  
  // Dan Kennedy式プラン情報の設定
  const planDetails = {
    standard: {
      name: 'スタンダードプラン',
      price: '月額480円',
      color: 'blue',
      value: '日常使いに余裕のあるポイント量',
      features: [
        '月900pt（栄養相談 最大225回相当）',
        'つむぎチャットなら 約900回お話できるよ',
        '血液検査分析が使えるようになるよ',
        'レシピ生成・成分表スキャンも自由に',
        '日次の利用制限なし'
      ]
    },
    premium: {
      name: 'プレミアムプラン',
      price: '月額980円',
      color: 'amber',
      value: 'たっぷり相談したい人向け',
      features: [
        '月1,800pt（栄養相談 最大450回相当）',
        '血液検査画像から自動分析 ⭐',
        '会話履歴 永久保存',
        '冷蔵庫・チラシレシピも気兼ねなく',
        '日次の利用制限なし'
      ]
    }
  };
  
  const targetPlan = planDetails[recommendedPlan];
  const urgencyColors = {
    high: 'bg-red-50 border-red-200',
    medium: 'bg-yellow-50 border-yellow-200',
    low: 'bg-blue-50 border-blue-200'
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* ヘッダー */}
        <div className={`p-6 border-b ${urgencyColors[urgency]}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-gray-700 mt-2 text-sm">{message}</p>
        </div>
        
        {/* 現在のプラン状況（利用制限の場合のみ） */}
        {trigger === 'usage_limit' && (
          <div className="p-4 bg-gray-50 border-b">
            <div className="text-center">
              <p className="text-sm text-gray-600">今月の利用状況</p>
              <div className="flex items-center justify-center mt-2">
                <div className="bg-red-100 rounded-full px-3 py-1">
                  <span className="text-red-800 font-semibold">{currentUsage} / {monthlyLimit} 回</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ポイント使い切り時の残量表示 */}
        {trigger === 'points_exhausted' && monthlyPoints != null && (
          <div className="p-4 bg-gray-50 border-b">
            <div className="text-center">
              <p className="text-sm text-gray-600">今月のポイント残量</p>
              <div className="flex items-center justify-center mt-2">
                <div className="bg-red-100 rounded-full px-3 py-1">
                  <span className="text-red-800 font-semibold">
                    {remainingPoints ?? 0} / {monthlyPoints} pt
                  </span>
                </div>
              </div>
              {requiredPoints != null && (
                <p className="text-xs text-gray-500 mt-2">
                  この機能には {requiredPoints}pt 必要だよ
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* おすすめプラン */}
        <div className="p-6">
          <div className={`border-2 border-${targetPlan.color}-200 rounded-lg p-4 bg-gradient-to-br from-${targetPlan.color}-50 to-white`}>
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">{targetPlan.name}</h3>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {targetPlan.price}
                <span className="text-sm font-normal text-gray-600">（税込）</span>
              </div>
              {recommendedPlan === 'premium' && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    🏆 最も人気
                  </span>
                </div>
              )}
            </div>
            
            {/* 機能一覧 */}
            <div className="space-y-2">
              {targetPlan.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span className="text-sm text-gray-700 flex-1">{feature}</span>
                </div>
              ))}
            </div>
            
            {/* 特別な機能の強調表示 */}
            {trigger === 'ocr_feature' && recommendedPlan === 'premium' && (
              <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-amber-600">🩸</span>
                  <span className="text-sm font-medium text-amber-800">
                    血液検査OCR機能で、より精密な栄養分析が可能に！
                  </span>
                </div>
              </div>
            )}
            
            {trigger === 'health_tracker' && recommendedPlan === 'premium' && (
              <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-amber-600">📊</span>
                  <span className="text-sm font-medium text-amber-800">
                    体調スコア記録機能で改善傾向を可視化！
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Standardサブボタン（離脱防止） */}
        {recommendedPlan === 'premium' && (
          <div className="px-6 pb-4">
            <p className="text-xs text-gray-500 text-center mb-2">
              まずは控えめに始めたい方は
            </p>
            <button
              onClick={() => {
                onClose();
                navigate('/plans');
              }}
              className="w-full border border-gray-300 text-gray-700 text-sm py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              スタンダードプラン（月480円）も見る
            </button>
          </div>
        )}

        {/* アクションボタン */}
        <div className="p-6 bg-gray-50 space-y-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/plans');
            }}
            className={`w-full ${
              targetPlan.color === 'amber' 
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
            } text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200`}
          >
            {targetPlan.name}にアップグレード
          </button>
          
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            後で検討する
          </button>
        </div>
        
        {/* お得情報 */}
        <div className="px-6 pb-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <span className="text-yellow-600">💡</span>
              <div className="flex-1">
                <p className="text-xs text-yellow-800 font-medium">
                  初回30日間返金保証付き
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  ご満足いただけない場合は、30日以内であれば全額返金いたします。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;